import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {INolimitAudioPlayer} from "@nolimitcity/slot-audio/bin/INolimitAudioPlayer";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {NolimitHowl} from "@nolimitcity/slot-audio/bin/NolimitHowl";
import {MentalSoundConfig} from "../MentalSoundConfig";
import {TweenLite} from "gsap";
import {Mental} from "../Mental";
import {MentalGameMode} from "../server/MentalGameMode";
import {NolimitSlotAudio} from "@nolimitcity/slot-audio/bin/NolimitSlotAudio";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";

export class SoundManager {
    private static currentLoop: string = "";
    private static prevMultSound: number = 0;

    private static get soundPlayer(): INolimitAudioPlayer {
        return <INolimitAudioPlayer>SlotGame.sound;
    }

    public static playAmbientLoop(name: string): void {
        this.currentLoop = name;

        this.soundPlayer.playTrack({name: this.currentLoop, volume: 0});
        this.soundPlayer.fadeTrack({
            name: this.currentLoop,
            from: 0,
            to: 1,
            duration: 1000
        });
    }

    public static playSoundEffect(name: string, repeat?: number, volume?: number): void {
        Logger.logDev("starting sound ", name);

        this.soundPlayer.playTrack({
            name: name,
            repeat: repeat,
            volume: volume
        });
    }

    public static stopSoundEffect(name: string): void {
        Logger.logDev("stopping sound ", name);

        this.soundPlayer.stopTrack(name);
    }

    public static fadeSound(name: string, from: number, to: number, duration: number): void {
        this.soundPlayer.fadeTrack({
            name: name,
            duration: duration,
            from: from,
            to: to
        });
    }

    public static playBigWinInit(): void {
        const player: NolimitHowl | undefined = this.soundPlayer.fetchSoundPlayer(this.currentLoop),
            fadeDuration: number = (Mental.model.gameData.mode.indexOf("FREESPIN") > -1 ? 0.4 : 0.3); // #332

        if (player && player.isPlaying) {
            player.fade(1, 0, (fadeDuration * 1000));
            TweenLite.to(this, {duration: fadeDuration}).eventCallback("onComplete", () => player.stop());
        }

        this.playSoundEffect(MentalSoundConfig.INIT_WIN_BIG_WIN, 0, 1);
    }

    public static playBigWinEnd(): void {
        this.playSoundEffect(MentalSoundConfig.BIG_WIN_END);

        // #332
        if (Mental.model.gameData.mode.indexOf("FREESPIN") > -1 && Mental.model.gameData.nextMode === MentalGameMode.NORMAL) {
            return;
        }

        const player: NolimitHowl | undefined = this.soundPlayer.fetchSoundPlayer(this.currentLoop);

        if (player) {
            player.fadeToMute(!(NolimitSlotAudio.apiPlugIn.settings.get(APISetting.MUSIC)), 0.05);
            player.fade(0, 1, 1000);
        }
    }

    public static startFSMusic(name: string): void {
        Logger.logDev("switching to ", name);

        const soundMain: NolimitHowl | undefined = this.soundPlayer.fadeTrack({
            name: this.currentLoop,
            duration: 300,
            to: 0
        });

        if (soundMain) {
            TweenLite.to(this, {duration: 0.3}).eventCallback("onComplete", () => soundMain.stop());
        }
        this.soundPlayer.playTrack({name: name});
        this.currentLoop = name;
    }

    public static upgradeFSMusic(name: string): void {
        Logger.logDev("upgrading fs music to ", name);

        const player: NolimitHowl | undefined = this.soundPlayer.fetchSoundPlayer(this.currentLoop);

        if (player && player.isPlaying) {
            player.fade(1, 0, 200);
            TweenLite.to(this, {duration: 0.2}).eventCallback("onComplete", () => player.stop());
        }

        this.soundPlayer.playTrack({name: name});
        this.currentLoop = name;
    }

    public static stopFSMusic(): void {
        Logger.logDev("switching to ", MentalSoundConfig.FS_END_LOOP);

        const fsTrack: NolimitHowl | undefined = this.soundPlayer.fadeTrack({
            name: this.currentLoop,
            duration: 300,
            from: 1,
            to: 0
        });

        if (fsTrack) {
            TweenLite.to(this, {duration: 0.3}).eventCallback("onComplete", () => fsTrack.stop());
        } else {
            this.soundPlayer.stopTrack(this.currentLoop);
        }

        this.fadeInLoop(MentalSoundConfig.FS_END_LOOP, 300);
    }

    public static playFSIntroLoop(name: string): void {
        Logger.logDev("switching to ", name);

        const soundMain: NolimitHowl | undefined = this.soundPlayer.fadeTrack({
            name: this.currentLoop,
            duration: 400,
            to: 0
        });

        if (soundMain) {
            TweenLite.to(this, {duration: 0.4}).eventCallback("onComplete", () => {
                soundMain.stop();
            });
        }
        this.fadeInLoop(name, 200);
    }

    public static onFsOutroBtnClick(): void {
        this.playSoundEffect(MentalSoundConfig.FS_BTN_CLICK);

        Logger.logDev("switching to ", MentalSoundConfig.MAIN_GAME_AMBIANCE);
        const soundMain: NolimitHowl | undefined = this.soundPlayer.fadeTrack({
            name: this.currentLoop,
            duration: 100,
            to: 0
        });

        if (soundMain) {
            TweenLite.to(this, {duration: 0.1}).eventCallback("onComplete", () => {
                soundMain.stop();
                this.fadeInLoop(MentalSoundConfig.MAIN_GAME_AMBIANCE, 100);
            });
        }
    }

    public static startWinCap(): void {
        this.playSoundEffect(MentalSoundConfig.WIN_CAP, 0, 1);

        const soundMain: NolimitHowl | undefined = this.soundPlayer.fadeTrack({
            name: this.currentLoop,
            duration: 200,
            to: 0
        });

        if (soundMain) {
            TweenLite.to(this, {duration: 0.2}).eventCallback("onComplete", () => {
                soundMain.stop();
            });
        }

        this.playSoundEffect(MentalSoundConfig.WIN_CAP_LOOP, Infinity, 1);
    }

    public static stopWinCap(): void {
        const winCapPlayer: NolimitHowl | undefined = this.soundPlayer.fetchSoundPlayer(MentalSoundConfig.WIN_CAP),
            winCapLoopPlayer: NolimitHowl | undefined = this.soundPlayer.fetchSoundPlayer(MentalSoundConfig.WIN_CAP_LOOP);

        // #332
        this.playSoundEffect(MentalSoundConfig.FS_BTN_CLICK);

        if (winCapPlayer && winCapPlayer.isPlaying) {
            Logger.logDev("stopping sound ", MentalSoundConfig.WIN_CAP);

            winCapPlayer.fade(1, 0, 0.2);
            TweenLite.to(this, {duration: 0.2}).eventCallback("onComplete", () => {
                winCapPlayer.stop();
            });
        }

        Logger.logDev("stopping sound ", MentalSoundConfig.WIN_CAP_LOOP);
        if (winCapLoopPlayer) {
            winCapLoopPlayer.fade(1, 0, 0.2);
            TweenLite.to(this, {duration: 0.2}).eventCallback("onComplete", () => {
                winCapLoopPlayer.stop();

                this.fadeInLoop(MentalSoundConfig.MAIN_GAME_AMBIANCE, 200);
            });
        }
    }

    public static playMultiplierSound(multValue: number): void {
        const deadActiveSound: NolimitHowl | undefined = SoundManager.soundPlayer.fadeTrack({
            name: MentalSoundConfig.DEAD_TURN_ACTIVE,
            duration: 300,
            to: 0
        });

        if (deadActiveSound) {
            TweenLite.to(this, {duration: 0.3}).eventCallback("onComplete", () => {
                SoundManager.stopSoundEffect(MentalSoundConfig.DEAD_TURN_ACTIVE);
            });
        }

        const multSounds: string[] = ["deadSymbol1", "deadSymbol2", "deadSymbol3", "deadSymbol4", "deadSymbol5"];

        multSounds.forEach((currSound: string) => {
            SoundManager.stopSoundEffect(currSound);
        });

        let soundToPlay: string = "";

        if (multValue > this.prevMultSound) {
            switch (multValue) {
                case 30:
                case 50:
                    soundToPlay = multSounds[1];
                    break;
                case 100:
                case 200:
                    soundToPlay = multSounds[2];
                    break;
                case 500:
                case 1000:
                    soundToPlay = multSounds[3];
                    break;
                case 9999:
                    soundToPlay = multSounds[4];
                    break;
                default:
                    soundToPlay = multSounds[0];
                    break;
            }
            SoundManager.playSoundEffect(soundToPlay);
            this.prevMultSound = multValue;
        }
    }

    public static reSetPrevMultSound(): void {
        this.prevMultSound = 0;
    }

    // #332
    public static stopXSplit(): void {
        const player: NolimitHowl | undefined = this.soundPlayer.fetchSoundPlayer(MentalSoundConfig.X_SPLIT);

        if (player && player.isPlaying) {
            Logger.logDev("stopping sound ", MentalSoundConfig.X_SPLIT);

            player.fade(1, 0, 0.1);
            TweenLite.to(this, {duration: 0.1}).eventCallback("onComplete", () => {
                player.stop();
            });
        }
    }

    private static fadeInLoop(name: string, dur: number): void {
        this.currentLoop = name;
        this.soundPlayer.playTrack({name: this.currentLoop, volume: 0});
        this.fadeSound(this.currentLoop, 0, 1, (dur / 1000));
    }
}